<script lang='ts'>
    import { cn } from '@/lib/utils';
    import type {HTMLInputAttributes} from 'svelte/elements'
    interface $$Props extends HTMLInputAttributes{
        mark?: number | null
        filled?: boolean
        warnFrom?: number | null
        errorFrom?: number | null
        thumbSize?: number
    }
    export let value: number = 0
    export let min: HTMLInputAttributes['min'] = 0
    export let max: HTMLInputAttributes['max'] = 100
    // A value to mark on the track, with its label in the "mark" slot below it.
    export let mark: number | null = null
    // Shade the track up to the thumb, and the rest of it pale.
    export let filled = false
    // Colour the track yellow from this value, and red from errorFrom, to its end (with filled, deeper up to the thumb).
    export let warnFrom: number | null = null
    export let errorFrom: number | null = null
    // The thumb's width and height in pixels. Content for it goes in the "thumb" slot, drawn over it.
    export let thumbSize = 30
    let className: string = ""
    export {className as class}

    // Where the mark sits along the track: the thumb's centre travels from half its width to the width less that.
    // Reactive so that the fill follows a change of the bounds, too.
    $: at = (v: number) => Number(max) <= Number(min) ? 0 : Math.min(1, Math.max(0, (v - Number(min))/(Number(max) - Number(min))))
    $: pos = (v: number) => `calc(${thumbSize/2}px + (100% - ${thumbSize}px) * ${at(v)})`
    $: markAt = mark == null || Number(max) <= Number(min) ? null : at(mark)
    // The track's colours from left to right, each to where the next starts: its zones (teal, then yellow from warnFrom and
    // red from errorFrom), each deep up to the thumb when filled and pale past it
    const TEAL = "var(--color-xert-teal)", PALE = "#d8e3e4", YELLOW = "#facc15", PALE_YELLOW = "#fef08a", RED = "#ef4444", PALE_RED = "#fecaca"
    $: stops = ((): [string, string][] => {
        if (Number(max) <= Number(min)) return []
        const zones: { from: number, deep: string, pale: string }[] = [{ from: Number(min), deep: TEAL, pale: filled ? PALE : TEAL }]
        if (warnFrom != null) zones.push({ from: warnFrom, deep: YELLOW, pale: PALE_YELLOW })
        if (errorFrom != null) zones.push({ from: errorFrom, deep: RED, pale: PALE_RED })
        if (!filled && zones.length === 1) return []
        const out: [string, string][] = []
        zones.forEach((zone, i) => {
            const to = i + 1 < zones.length ? zones[i + 1].from : Number(max)
            const end = i + 1 < zones.length ? pos(to) : "100%"
            if (filled && value > zone.from && value < to) out.push([zone.deep, pos(value)], [zone.pale, end])
            else out.push([filled && value >= to ? zone.deep : zone.pale, end])
        })
        return out
    })()
    $: background = stops.length ? `--slider-background: linear-gradient(to right, ${stops.map(([color, to], i) => `${color} ${i ? stops[i - 1][1] : "0%"}, ${color} ${to}`).join(", ")});` : ""
</script>

<!--
This is only a wrapper for the styling of the input component. A label tag is
still required for accessibility guidelines.
-->

<div class={cn("w-full", markAt != null && "relative", className)}>
    {#if $$slots.thumb}
        <!-- The thumb's content sits over it and lets the pointer through to the input -->
        <div class="relative">
            <input {...$$restProps} {min} {max} class="select-slider block w-full my-3 min-h-[11px]" style="{background} --thumb-size: {thumbSize}px" type="range" bind:value={value} on:input on:change/>
            <div class="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none" style="left: {pos(value)}; width: {thumbSize}px; height: {thumbSize}px">
                <slot name="thumb"/>
            </div>
        </div>
    {:else}
        <input {...$$restProps} {min} {max} class="select-slider my-3 min-h-[11px]" style="{background} --thumb-size: {thumbSize}px" type="range" bind:value={value} on:input on:change/>
    {/if}
    {#if markAt != null}
        <!-- A triangle just below the thumb points up at the mark; the label below it is centred on the mark, sliding to
             stay inside the track at either end. -->
        <div class="relative h-3 -mt-0.5" aria-hidden="true">
            <div class="absolute top-0 w-0 h-0 -translate-x-1/2 border-solid border-x-[7px] border-x-transparent border-b-[10px] border-b-slate-700 border-t-0"
                style="left: {pos(mark ?? 0)}"/>
        </div>
        <div class="relative w-max" style="left: {pos(mark ?? 0)}; transform: translateX(-{markAt*100}%)">
            <slot name="mark"/>
        </div>
    {/if}
</div>

<style lang="scss">
    .select-slider {
        -webkit-appearance: none;
        appearance: none;
        background: var(--slider-background, var(--color-xert-teal));
        cursor: pointer;
        border-radius: 99px !important;
        height: 8px;

        &:focus{
            outline:none;
        }

        &::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: var(--thumb-size, 30px);
            height: var(--thumb-size, 30px);
            background: #ffffff;
            border-radius: 50% !important;
            border: 1px solid rgba(172, 172, 172, 0.98);
            cursor: pointer;
            box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
        }

        &::-moz-range-thumb{
            width: var(--thumb-size, 30px);
            height: var(--thumb-size, 30px);
            background: #ffffff;
            border-radius: 50% !important;
            border: 1px solid rgba(193, 193, 193, 0.98);
            cursor: pointer;
            box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
            position: relative;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
    }
</style>
